import { Accordion, Box, ChakraProps, Flex, Text } from "@chakra-ui/react";
import { kebabCase } from "lodash";
import { useRouter } from "next/router";
import { Fragment, useEffect, useMemo, useRef, useState, VFC } from "react";

import { config, NavItem } from "./config";
import { NavFolder } from "./nav-folder";
import { NavLink } from "./nav-link";

import { User } from "api/get-user";
import { CTALinks } from "components/cta-links";

const subNavLeftPadding = 6;

export const navItemProps: ChakraProps = {
  cursor: "pointer",
  userSelect: "none",
  border: "1px",
  borderColor: "white",
  borderRadius: "base",
  fontSize: "md",
  fontWeight: "normal",
  py: 1,
  _focus: {
    outline: "none !important",
  },
  _focusVisible: {
    outline: "none",
    boxShadow: "outline",
  },
};

type Props = {
  destinations?: Array<any> | undefined;
  sources?: Array<any> | undefined;
  user?: User;
  toggle?: () => void;
};

export const Nav: VFC<Readonly<Props>> = (props) => {
  const router = useRouter();
  const [accordionIndex, setAccordionIndex] = useState<number[]>([]);

  const handleAccordionChange = (expandedIndexes: number[]) => {
    setAccordionIndex(expandedIndexes);
  };

  // The index of the currently active top-level nav accordion tem.
  const currentRouteNavIndex = useMemo(() => {
    const currentPath = `/${router.pathname.split("/")[1]}`;
    const visibleConfig = props.user?.private
      ? config
      : config.filter((item) => !item.private);
    return visibleConfig.findIndex(
      (item) => (item.path || `/${kebabCase(item.label)}`) === currentPath
    );
  }, [router.pathname, props.user?.private]);

  useEffect(() => {
    if (currentRouteNavIndex !== -1) {
      setAccordionIndex([currentRouteNavIndex]);
    }
  }, [currentRouteNavIndex]);

  const processNav = (nav: NavItem[], depth: number, pathPrefix = "") => {
    return nav.map((item) => processNavItem(item, depth, pathPrefix));
  };

  const processNavItem = (item: NavItem, depth: number, pathPrefix = "") => {
    const path = `${pathPrefix}${item.path ?? `/${kebabCase(item.label)}`}`;

    if (item.private && !props.user?.private) {
      return null;
    }

    if (depth === 1) {
      const isActive = router.pathname.startsWith(path);

      return (
        <Box as="li" key={item.label}>
          <NavFolder isActive={isActive} item={item}>
            {item.nav && processNav(item.nav, depth + 1, path)}
          </NavFolder>
        </Box>
      );
    }
    const isGroupLabel = depth === 2 && item.nav?.length;

    if (isGroupLabel) {
      return (
        <Fragment key={item.label}>
          <Text
            as="li"
            textStyle="uppercase"
            ml={subNavLeftPadding}
            mt={1}
            pt={4}
            pb={2}
            borderTop="1px"
            borderColor="base.divider"
          >
            {item.label}
          </Text>
          <Box as="ul" listStyleType="none">
            {item.nav && processNav(item.nav, depth + 1, pathPrefix)}
          </Box>
        </Fragment>
      );
    }

    const isActive =
      router.asPath === path ||
      (router.pathname === "/getting-started/welcome" &&
        path === "/getting-started/welcome");

    return (
      <Box as="li" key={item.label}>
        <NavLink
          item={item}
          isActive={isActive}
          path={path}
          subNavLeftPadding={subNavLeftPadding}
          toggle={props.toggle}
        />
      </Box>
    );
  };

  const navConfig = useMemo(
    () =>
      config.map((item) => {
        if (item.label === "Sources") {
          return {
            ...item,
            nav: props.sources
              ?.filter(
                ({ status }) => status === "released" || status === "alpha"
              )
              .map(({ name, slug }) => ({
                label: name,
                path: `/${slug.replace("-source", "")}`,
              })),
          };
        }
        if (item.label === "Destinations") {
          return {
            ...item,
            nav: props.destinations
              ?.filter(
                ({ slug, status }) =>
                  slug !== "hightouch-personalization-api" &&
                  (status === "released" || status === "alpha")
              )
              .map(({ name, slug }) => {
                let slugOverride = undefined;
                let nameOverride = undefined;
                if (slug === "webhook-v2") {
                  slugOverride = "http-request";
                }
                if (slug === "hightouch-personalization-api") {
                  slugOverride = "personalization-api";
                }
                if (name === "Google Sheets - User Account") {
                  nameOverride = "Google Sheets";
                }
                return {
                  label: nameOverride ? nameOverride : name,
                  path: `/${
                    slugOverride
                      ? slugOverride
                      : slug.replace("-destination", "")
                  }`,
                };
              }),
          };
        }
        return item;
      }),
    [props.destinations, props.sources]
  );

  const content = useMemo(
    () => processNav(navConfig, 1),
    [navConfig, router.pathname, processNav, props.user?.private]
  );

  return (
    <Flex as="nav" direction="column" width="100%" p={{ base: 2, md: 4 }}>
      <Accordion
        allowToggle
        index={accordionIndex}
        as="ul"
        listStyleType="none"
        onChange={handleAccordionChange}
      >
        {content}
      </Accordion>

      <CTALinks
        includeChangelog
        hideSecondary
        primary="signup"
        display={{ base: "flex", md: "none" }}
        mt={8}
        mx={6}
      />
    </Flex>
  );
};
